import { useEffect, useRef, useState } from 'react';
import { normalizeTagLabel } from '../../lib/tagLabel.js';

/**
 * TagCombobox — the free-text tag entry (TRACK-B-1b §3.1).
 *
 * Renders a NATIVE <input> and a native menu surface carrying oc-combobox__* classes,
 * NOT the skill's <TextField>/<Menu> as JSX: neither component's declared-prop allowlist
 * contains onKeyDown / onFocus / role / aria-*, so mounting them would fail the adherence
 * gate at --max-warnings 0 by construction. This is the established port idiom — only
 * VALUES transfer, as ds.css:3-5 states and as Badge/SearchBar/Table already did.
 *
 * The root carries `outcrm-scope` ITSELF: DS tokens only resolve under that class, and
 * three of this control's four mounts (CreateBloggerModal, CsvImportModal, BulkTagModal)
 * sit outside any such subtree. Precedent: monitoring.css:41, lead-card.css:61.
 *
 * There is NO clickable create affordance. The hint row is a non-interactive <div>:
 * the manager is told the text will become a new tag, and the creation happens on
 * submit, server-side.
 */
export function TagCombobox ({
  value,
  onChange,
  suggestions = [],
  onCommit,
  placeholder = 'Тег',
  disabled = false,
  compact = false,
  maxLength = 64,
}) {
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const shown = suggestions;
  const normalizedValue = normalizeTagLabel(value);
  const picked = shown.find(t => normalizeTagLabel(t?.label) === normalizedValue);

  const pick = (t) => {
    onChange(t.label);
    setOpen(false);
    setHighlightIdx(-1);
    onCommit?.(t.label);
  };

  const handleInput = (e) => {
    // Opens on the EVENT value, never on the prop: inside this handler `value` and
    // `suggestions` are still the PRE-keystroke props, so reading them here would leave
    // the menu closed on the first character typed against an empty directory — and a
    // closed menu hands Enter straight to CreateBloggerModal's <form onSubmit>.
    const next = e.target.value;
    onChange(next);
    setHighlightIdx(-1);
    setOpen(Boolean(next.trim() || suggestions.length));
  };

  const handleFocus = () => {
    if (value?.trim() || suggestions.length) setOpen(true);
  };

  const handleKeyDown = (e) => {

    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => Math.min(prev + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      // preventDefault whenever the menu is open — a stray Enter must never submit the
      // host form while the manager is still choosing.
      e.preventDefault();
      if (highlightIdx >= 0 && shown[highlightIdx]) {
        pick(shown[highlightIdx]);
      } else {
        setOpen(false);
        onCommit?.(value.trim());
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const fieldClass = [
    'oc-combobox__field',
    compact && 'oc-combobox__field--compact',
    disabled && 'oc-combobox__field--disabled',
  ].filter(Boolean).join(' ');

  return (
    <div className='outcrm-scope oc-combobox' ref={containerRef}>
      <div className={fieldClass}>
        {picked && <span className='oc-combobox__dot' style={{ background: picked.color }} />}
        <input
          className='oc-combobox__input'
          type='text'
          role='combobox'
          aria-expanded={open}
          aria-autocomplete='list'
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          autoComplete='off'
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
        />
      </div>
      {open && (
        <div className='oc-combobox__menu' role='listbox'>
          {shown.map((t, idx) => (
            // type='button' is LOAD-BEARING: CreateBloggerModal.jsx:155 is a real
            // <form onSubmit>, and an untyped <button> inside it defaults to submit —
            // which would create the lead on a tag click (TRACK-B-1 round-1 BLOCKER).
            <button
              type='button'
              role='option'
              aria-selected={idx === highlightIdx}
              key={t.id}
              className={`oc-combobox__item${idx === highlightIdx ? ' oc-combobox__item--active' : ''}`}
              onMouseEnter={() => setHighlightIdx(idx)}
              onClick={() => pick(t)}
            >
              <span className='oc-combobox__dot' style={{ background: t.color }} />
              {t.label}
            </button>
          ))}
          {shown.length === 0 && value.trim() && (
            <div className='oc-combobox__hint'>Новый тег: {value.trim()}</div>
          )}
        </div>
      )}
    </div>
  );
}
